import { Component } from '@theme/component';
import { CartUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

/** Shared across instances so two sync elements never mutate the cart at once. */
let syncInFlight = false;

/**
 * Collects the section ids of all cart-items-component elements on the page.
 * @returns {string[]}
 */
function getCartSectionIds() {
  const ids = /** @type {string[]} */ ([]);
  document.querySelectorAll('cart-items-component').forEach((item) => {
    if (item instanceof HTMLElement && item.dataset.sectionId) {
      ids.push(item.dataset.sectionId);
    }
  });
  return ids;
}

/**
 * Cart reward sync component.
 * Headless element rendered by snippets/cart-reward-sync.liquid.
 * The server decides which reward variants to add or remove and exposes them
 * as data attributes; this element executes the cart mutations.
 */
class CartRewardSync extends Component {
  static observedAttributes = ['data-add-variants', 'data-remove-variants'];

  #scheduled = false;

  /**
   * Called on upgrade for present attributes and whenever a section morph changes them.
   * @param {string} name
   * @param {string|null} oldValue
   * @param {string|null} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    if (!newValue || this.#scheduled) return;

    this.#scheduled = true;
    queueMicrotask(() => {
      this.#scheduled = false;
      this.#sync();
    });
  }

  /**
   * Removes outdated reward variants and adds earned ones, then notifies the cart.
   */
  async #sync() {
    if (syncInFlight) return;

    const removeKeys = (this.dataset.removeVariants || '').split(',').filter(Boolean);
    const addIds = (this.dataset.addVariants || '').split(',').filter(Boolean);

    if (!removeKeys.length && !addIds.length) return;

    syncInFlight = true;
    const sections = getCartSectionIds().join(',');
    let lastResponse = null;

    try {
      if (removeKeys.length) {
        const updates = {};
        for (const key of removeKeys) {
          updates[key] = 0;
        }

        const response = await fetch(Theme.routes.cart_update_url, {
          ...fetchConfig('json', { body: JSON.stringify({ updates, sections }) }),
        });

        if (!response.ok) {
          console.error('[cart-reward-sync] Failed to remove reward', response.status);
        } else {
          lastResponse = await response.json();
        }
      }

      if (addIds.length) {
        const items = addIds.map((id) => ({ id: Number(id), quantity: 1 }));

        const response = await fetch(Theme.routes.cart_add_url, {
          ...fetchConfig('json', { body: JSON.stringify({ items, sections }) }),
        });

        if (!response.ok) {
          console.error('[cart-reward-sync] Failed to add reward', response.status);
        } else {
          lastResponse = await response.json();
        }
      }

      if (!lastResponse?.sections) return;

      const firstSectionHtml = Object.values(lastResponse.sections)[0];
      const itemCountText = firstSectionHtml
        ? new DOMParser()
            .parseFromString(String(firstSectionHtml), 'text/html')
            .querySelector('[ref="cartItemCount"]')?.textContent
        : null;
      const itemCount = itemCountText ? parseInt(itemCountText, 10) : 0;

      document.dispatchEvent(
        new CartUpdateEvent({}, 'cart-reward-sync', {
          source: 'cart-reward-sync',
          itemCount,
          sections: lastResponse.sections,
        }),
      );
    } catch (error) {
      console.error('[cart-reward-sync]', error);
    } finally {
      syncInFlight = false;
    }
  }
}

if (!customElements.get('cart-reward-sync')) {
  customElements.define('cart-reward-sync', CartRewardSync);
}

/**
 * Cart reward tiers component.
 * Product rewards are added and removed automatically by <cart-reward-sync>.
 * The add button remains as a manual fallback (e.g. while the auto-add is in flight).
 * Progress bar and tier states are updated server-side via section re-rendering.
 */
class CartRewardTiers extends Component {
  /**
   * Adds a reward variant to cart. Called by the global click handler.
   * @param {Element} button - The clicked add button
   */
  async addReward(button) {
    const variantId = /** @type {HTMLElement} */ (button).dataset.variantId;

    if (!variantId || /** @type {HTMLButtonElement} */ (button).disabled) return;

    button.setAttribute('aria-busy', 'true');

    try {
      const body = JSON.stringify({
        items: [{ id: Number(variantId), quantity: 1 }],
      });

      const response = await fetch(Theme.routes.cart_add_url, {
        ...fetchConfig('json', { body }),
      });

      if (!response.ok) {
        console.error('[cart-reward-tiers] Failed to add reward', response.status);
        return;
      }

      const cart = await (await fetch('/cart.json')).json();

      document.dispatchEvent(
        new CartUpdateEvent(cart, 'cart-reward-tiers', {
          source: 'cart-reward-tiers',
          itemCount: cart.item_count,
        }),
      );
    } catch (error) {
      console.error('[cart-reward-tiers]', error);
    } finally {
      button.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('cart-reward-tiers')) {
  customElements.define('cart-reward-tiers', CartRewardTiers);
}

/**
 * Global click handler for reward tier add buttons.
 * Uses event delegation at document level so it works regardless of
 * DOM morphing, element replacement, or custom element lifecycle.
 */
document.addEventListener('click', (event) => {
  const button = /** @type {Element} */ (event.target).closest(
    'cart-reward-tiers [data-variant-id]',
  );
  if (!button) return;

  const component = /** @type {CartRewardTiers | null} */ (button.closest('cart-reward-tiers'));
  if (component?.addReward) {
    component.addReward(button);
  }
});
